/**
 * ArticleImageSearch Component
 * 
 * Main container for image search and selection.
 * Supports AI generation (Fal.ai) and stock image search (Pixabay, Pexels).
 * When both AI and stock sources are selected, fires both requests in parallel.
 * AI-generated images appear first, followed by stock search results.
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Divider,
  Alert,
} from '@mui/material';
import {
  Image as ImageIcon,
} from '@mui/icons-material';
import { ImageSearchForm } from './ImageSearchForm';
import { ImageResultsGrid } from './ImageResultsGrid';
import { SelectedImagesList } from './SelectedImagesList';
import { imageService } from '../../api/imageService';
import type { 
  ArticleImageSearchProps, 
  ImageSource, 
  Image, 
  ArticleImage,
} from './types';
import { 
  MAX_IMAGES_PER_ARTICLE, 
  IMAGES_PER_PAGE, 
  hasAnyAISource,
  hasAnyStockSource,
  toApiStockSource,
  THEME_COLORS,
} from './constants';

export const ArticleImageSearch: React.FC<ArticleImageSearchProps> = ({
  selectedImages,
  onImagesChange,
  maxImages = MAX_IMAGES_PER_ARTICLE,
  disabled = false,
}) => {
  // Search state
  const [query, setQuery] = useState('');
  const [selectedSources, setSelectedSources] = useState<ImageSource[]>([]);

  // Separate AI and stock results so pagination only affects stock
  const [aiResults, setAIResults] = useState<Image[]>([]);
  const [stockResults, setStockResults] = useState<Image[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [lastSearchQuery, setLastSearchQuery] = useState('');

  // Derived mode flags
  const hasAI = hasAnyAISource(selectedSources);
  const hasStock = hasAnyStockSource(selectedSources);
  const onlyAI = hasAI && !hasStock;

  // Combined results: AI first, then stock
  const results = useMemo(() => [...aiResults, ...stockResults], [aiResults, stockResults]);

  // Create a Set of selected image IDs for quick lookup
  const selectedIds = useMemo(() => new Set(selectedImages.map(img => img.id)), [selectedImages]);
  const isSelectionDisabled = selectedImages.length >= maxImages;

  /**
   * Fetch stock image results.
   * Returns the response so the caller can decide how to set state.
   */
  const fetchStockResults = useCallback(async (searchQuery: string, page: number) => {
    const apiSource = toApiStockSource(selectedSources);
    return imageService.search({
      query: searchQuery,
      source: apiSource,
      page,
      per_page: IMAGES_PER_PAGE,
    });
  }, [selectedSources]);

  /**
   * Fetch AI generation result.
   * Returns an Image object so the caller can decide how to set state.
   */
  const fetchAIResult = useCallback(async (prompt: string): Promise<Image> => {
    const response = await imageService.generate({ prompt });
    return {
      id: response.id,
      source: response.source,
      url: response.url,
      thumbnail_url: response.thumbnail_url,
      preview_url: response.url,
      width: response.width,
      height: response.height,
      tags: prompt.split(' ').slice(0, 5),
      author: 'Fal.ai',
      author_url: 'https://fal.ai',
      license: 'AI Generated',
      download_url: response.url,
      s3_key: response.s3_key,
    };
  }, []);

  /**
   * Handle main search submission.
   * Fires AI and/or stock requests in parallel depending on selected sources.
   */
  const handleSearch = useCallback(async () => {
    if (query.trim().length < 2 || selectedSources.length === 0) return;

    setIsLoading(true);
    setError(null);
    setAIResults([]);
    setStockResults([]);

    const promises: Promise<void>[] = [];

    // AI generation
    if (hasAI) {
      promises.push(
        fetchAIResult(query)
          .then(img => setAIResults([img]))
          .catch(err => {
            console.error('AI generation failed:', err);
            // Don't set a hard error if stock search also running — just log
            if (!hasStock) {
              setError(err.message || 'Failed to generate image. Please try again.');
            }
          })
      );
    }

    // Stock search
    if (hasStock) {
      promises.push(
        fetchStockResults(query, 1)
          .then(response => {
            setStockResults(response.data || []);
            setCurrentPage(response.pagination.page);
            setTotalPages(response.pagination.total_pages);
          })
          .catch(err => {
            console.error('Image search failed:', err);
            if (!hasAI) {
              setError(err.message || 'Failed to search images. Please try again.');
            }
          })
      );
    }

    await Promise.allSettled(promises);
    setLastSearchQuery(query);
    setIsLoading(false);
  }, [query, selectedSources, hasAI, hasStock, fetchAIResult, fetchStockResults]);

  /**
   * Handle page change — only re-fetches stock results, keeps AI results pinned.
   */
  const handlePageChange = useCallback(async (page: number) => {
    if (!lastSearchQuery || !hasStock) return;

    setIsLoading(true);
    try {
      const response = await fetchStockResults(lastSearchQuery, page);
      setStockResults(response.data || []);
      setCurrentPage(response.pagination.page);
      setTotalPages(response.pagination.total_pages);
    } catch (err: any) {
      console.error('Image search failed:', err);
      setError(err.message || 'Failed to search images. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [lastSearchQuery, hasStock, fetchStockResults]);

  /**
   * Handle image selection toggle
   * For stock images (pixabay/pexels), persists to S3 before adding to selection.
   * For AI images (fal_ai), they're already in S3, so just add to selection.
   */
  const handleToggleSelect = useCallback(async (image: Image) => {
    const isSelected = selectedIds.has(image.id);

    if (isSelected) {
      const newSelection = selectedImages.filter(img => img.id !== image.id);
      onImagesChange(newSelection);
      return;
    }

    if (selectedImages.length >= maxImages) {
      return;
    }

    // For stock images, persist to S3 first
    if ((image.source === 'pixabay' || image.source === 'pexels') && !image.s3_key) {
      setIsLoading(true);
      setError(null);
      
      try {
        // Persist to S3 (download from provider and upload to S3)
        const persistResult = await imageService.persistStockImage(
          image.source,
          image.id,
          image.download_url || image.url
        );

        // Create article image with S3 metadata
        const articleImage: ArticleImage = {
          id: image.id,
          source: image.source,
          url: persistResult.url,
          width: image.width,
          height: image.height,
          tags: image.tags,
          author: image.author,
          author_url: image.author_url,
          license: image.license,
          s3_key: persistResult.s3_key,
          keywords: lastSearchQuery || query,
          selected_at: Date.now(),
        };
        
        onImagesChange([...selectedImages, articleImage]);
      } catch (err: any) {
        console.error('Failed to persist stock image:', err);
        setError(err.message || 'Failed to save image. Please try again.');
        // Don't add to selection if persistence fails
      } finally {
        setIsLoading(false);
      }
    } else {
      // AI images are already in S3, or image already has s3_key
      const articleImage: ArticleImage = {
        id: image.id,
        source: image.source,
        url: image.url,
        width: image.width,
        height: image.height,
        tags: image.tags,
        author: image.author,
        author_url: image.author_url,
        license: image.license,
        s3_key: image.s3_key,
        keywords: lastSearchQuery || query,
        selected_at: Date.now(),
      };
      onImagesChange([...selectedImages, articleImage]);
    }
  }, [selectedIds, selectedImages, onImagesChange, maxImages, lastSearchQuery, query]);

  /**
   * Handle image removal from selection
   */
  const handleRemoveImage = useCallback((id: string) => {
    const newSelection = selectedImages.filter(img => img.id !== id);
    onImagesChange(newSelection);
  }, [selectedImages, onImagesChange]);

  /**
   * Handle sources change
   */
  const handleSourcesChange = useCallback((newSources: ImageSource[]) => {
    setSelectedSources(newSources);
  }, []);

  // Build results header text
  const resultsHeader = useMemo(() => {
    if (results.length === 0 || isLoading) return '';
    if (aiResults.length > 0 && stockResults.length > 0) {
      return `AI generated + search results for "${lastSearchQuery}"`;
    }
    if (aiResults.length > 0) return 'Generated Image';
    return `Search results for "${lastSearchQuery}"`;
  }, [results.length, isLoading, aiResults.length, stockResults.length, lastSearchQuery]);

  return (
    <Box
      sx={{
        mt: 3,
        p: 3,
        borderRadius: 2,
        bgcolor: THEME_COLORS.cardBackground,
        border: `1px solid ${THEME_COLORS.border}`,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <ImageIcon sx={{ color: THEME_COLORS.accent }} />
        <Typography variant="h6" sx={{ color: THEME_COLORS.textPrimary, fontWeight: 600 }}>
          Article Images
        </Typography>
      </Box>

      <Typography variant="body2" sx={{ color: THEME_COLORS.textSecondary, mb: 3 }}>
        Search for stock images or generate AI images to attach to this article.
        You can select up to {maxImages} images.
      </Typography>

      {/* Search Form */}
      <ImageSearchForm
        query={query}
        onQueryChange={setQuery}
        selectedSources={selectedSources}
        onSourcesChange={handleSourcesChange}
        onSearch={handleSearch}
        isLoading={isLoading}
        disabled={disabled}
      />

      {/* Results Grid */}
      {(results.length > 0 || isLoading || error) && (
        <>
          <Divider sx={{ my: 3, borderColor: THEME_COLORS.border }} />
          
          {/* Results header */}
          {resultsHeader && (
            <Typography variant="subtitle2" sx={{ color: THEME_COLORS.textSecondary, mb: 1 }}>
              {resultsHeader}
            </Typography>
          )}

          <ImageResultsGrid
            images={results}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            selectionDisabled={isSelectionDisabled}
            isLoading={isLoading}
            currentPage={currentPage}
            totalPages={onlyAI ? 0 : totalPages}
            onPageChange={handlePageChange}
            error={error}
          />
        </>
      )}

      {/* Selected Images */}
      <Divider sx={{ my: 3, borderColor: THEME_COLORS.border }} />
      <SelectedImagesList
        images={selectedImages}
        onRemove={handleRemoveImage}
        maxImages={maxImages}
      />

      {/* Selection disabled warning */}
      {isSelectionDisabled && results.length > 0 && (
        <Alert
          severity="warning"
          sx={{
            mt: 2,
            bgcolor: 'rgba(245, 158, 11, 0.1)',
            color: THEME_COLORS.warning,
            border: `1px solid ${THEME_COLORS.warning}`,
            '& .MuiAlert-icon': {
              color: THEME_COLORS.warning,
            },
          }}
        >
          Maximum {maxImages} images selected. Remove an image to add more.
        </Alert>
      )}
    </Box>
  );
};

export default ArticleImageSearch;
